<template>
   <div class="multi-select">
      <button
         :id="id"
         type="button"
         class="multi-select__trigger"
         :class="{
            'multi-select__trigger--open': isOpen,
            'multi-select__trigger--invalid': state === false,
         }"
         :disabled="disabled"
         aria-haspopup="true"
         :aria-expanded="isOpen ? 'true' : 'false'"
         @click="toggleOpen"
         @keydown.esc="close"
      >
         <span
            class="multi-select__summary"
            :class="{'multi-select__summary--placeholder': !value.length}"
         >
            {{ summaryText }}
         </span>
         <custom-icon
            icon="icon-chevron-down"
            :width="14"
            :height="14"
            class="multi-select__chevron"
         />
      </button>

      <div v-if="isOpen" class="multi-select__panel" role="group" :aria-label="placeholder">
         <label v-if="showSelectAll" class="multi-select__option multi-select__option--select-all">
            <input
               type="checkbox"
               class="multi-select__checkbox"
               :checked="isAllSelected"
               @change="toggleSelectAll"
            />
            <span class="multi-select__option-label">Select All</span>
         </label>
         <label v-for="option in options" :key="option.value" class="multi-select__option">
            <input
               type="checkbox"
               class="multi-select__checkbox"
               :checked="isSelected(option.value)"
               @change="toggleOption(option.value)"
            />
            <span class="multi-select__option-label">{{ option.text }}</span>
         </label>
      </div>
   </div>
</template>

<script>
export default {
   name: 'MultiSelect',

   props: {
      value: {
         type: Array,
         default: () => [],
      },
      options: {
         type: Array,
         default: () => [],
      },
      placeholder: {
         type: String,
         default: 'Select...',
      },
      disabled: {
         type: Boolean,
         default: false,
      },
      id: {
         type: String,
         default: null,
      },
      showSelectAll: {
         type: Boolean,
         default: false,
      },
      /** Matches BootstrapVue's `state` convention: false = invalid, null/true = neutral. */
      state: {
         type: Boolean,
         default: null,
      },
   },

   data() {
      return {
         isOpen: false,
      };
   },

   computed: {
      summaryText() {
         return this.value.length ? `${this.value.length} selected` : this.placeholder;
      },

      isAllSelected() {
         return this.options.length > 0 && this.value.length === this.options.length;
      },
   },

   methods: {
      toggleOpen() {
         if (this.disabled) return;
         this.isOpen = !this.isOpen;
      },

      close() {
         this.isOpen = false;
      },

      isSelected(optionValue) {
         return this.value.includes(optionValue);
      },

      toggleOption(optionValue) {
         const next = this.isSelected(optionValue)
            ? this.value.filter((v) => v !== optionValue)
            : [...this.value, optionValue];
         this.$emit('input', next);
      },

      toggleSelectAll() {
         this.$emit('input', this.isAllSelected ? [] : this.options.map(({value}) => value));
      },

      handleClickOutside(event) {
         if (this.isOpen && !this.$el.contains(event.target)) {
            this.close();
         }
      },
   },

   mounted() {
      document.addEventListener('mousedown', this.handleClickOutside);
   },

   beforeDestroy() {
      document.removeEventListener('mousedown', this.handleClickOutside);
   },
};
</script>

<style scoped lang="scss">
.multi-select {
   position: relative;
   width: 100%;
}

.multi-select__trigger {
   display: flex;
   align-items: center;
   justify-content: space-between;
   width: 100%;
   padding: 6px 12px;
   font-size: 13px;
   font-weight: 400;
   line-height: 1.5;
   color: $body-color;
   background-color: #fff;
   border: 1px solid $input-border-color;
   border-radius: 6px;
   cursor: pointer;
   transition: border-color 150ms ease-out;

   &--open,
   &:focus {
      border-color: $primary;
      outline: none;
   }

   &--invalid {
      border-color: $danger;
   }

   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
}

.multi-select__summary {
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;

   &--placeholder {
      color: $input-placeholder-color;
   }
}

.multi-select__chevron {
   display: inline-flex;
   align-items: center;
   justify-content: center;
   flex-shrink: 0;
   width: 14px;
   height: 14px;
   margin-left: 8px;
   color: $input-placeholder-color;
   transform-origin: center;
   transition: transform 150ms ease-out;
}

.multi-select__trigger--open .multi-select__chevron {
   transform: rotate(180deg);
}

.multi-select__panel {
   position: absolute;
   z-index: 100;
   top: calc(100% + 4px);
   left: 0;
   width: 100%;
   max-height: 260px;
   overflow-y: auto;
   display: flex;
   flex-direction: column;
   gap: 14px;
   padding: 12px;
   background-color: #fff;
   border: 1px solid $gray-300;
   border-radius: 6px;
   box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12);
}

.multi-select__option {
   display: flex;
   align-items: center;
   gap: 12px;
   margin: 0;
   padding: 4px;
   border-radius: 4px;
   cursor: pointer;
   user-select: none;

   &:hover {
      background-color: $gray-100;
   }

   &--select-all {
      padding-bottom: 12px;
      margin-bottom: 4px;
      border-bottom: 1px solid $gray-300;
      font-weight: 600;
   }
}

.multi-select__checkbox {
   flex-shrink: 0;
   width: 18px;
   height: 18px;
   margin: 0;
   accent-color: $primary;
   cursor: pointer;
}

.multi-select__option-label {
   font-size: 13px;
   color: $body-color;
}
</style>
